import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useAppState } from "main/context/app-context";
import { navItems } from "utils/nav-items";
import { mapMenusToNavItems, NavItem } from "utils/menu-mapper";
import {
  Nav,
  NavList,
  NavListItem,
  NavListSubMenu,
  SubMenuItem,
} from "./nav.styles";
import ArrowDown from "assets/icons/arrow-down.svg";
import MegaMenu from "components/megaMenu";

const MAX_VISIBLE_ITEMS = 6;

function useOutSiteClickAlert(ref: any, action: any) {
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (ref.current && !ref.current.contains(event.target)) {
        action();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });
}

export const NavBarComponent = () => {
  const navigate = useNavigate();
  const { menus, menusError, applicationData } = useAppState();
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [megaMenuOpen, setMegaMenuOpen] = useState<boolean>(false);
  const subMenuRef = useRef(null);

  const emptyActiveTab = () => {
    setActiveTab(null);
    setMegaMenuOpen(false);
  };

  useOutSiteClickAlert(subMenuRef, emptyActiveTab);

  // Aplicar overrides de serviceUrls aos itens de navegação
  const applyServiceUrlOverrides = (items: NavItem[]): NavItem[] => {
    const urlDiarioOficial = applicationData?.entity?.serviceUrls?.urlDiarioOficial;

    if (!urlDiarioOficial) return items;

    return items.map(item => {
      // Verificar se é o menu Diário Oficial (case-insensitive)
      if (item.label.toUpperCase().includes("DIÁRIO OFICIAL")) {
        const isExternal = urlDiarioOficial.startsWith('http://') || urlDiarioOficial.startsWith('https://');
        return {
          ...item,
          route: urlDiarioOficial,
          isExternal
        };
      }
      return item;
    });
  };

  // Mapear menus da API para NavItem, fallback para nav-items em erro/vazio
  const navigationItems: NavItem[] = React.useMemo(() => {
    let items: NavItem[] = [];

    if (menusError || !menus || menus.length === 0) {
      items = navItems;
    } else {
      items = mapMenusToNavItems(menus);
    }

    // Aplicar overrides de serviceUrls
    return applyServiceUrlOverrides(items);
  }, [menus, menusError, applicationData?.entity?.serviceUrls]);

  // Dividir: primeiros 6 visíveis + restantes para MegaMenu
  const visibleItems = navigationItems.slice(0, MAX_VISIBLE_ITEMS);
  const overflowItems = navigationItems.slice(MAX_VISIBLE_ITEMS);
  const hasMegaMenu = overflowItems.length > 0;

  const handleNavigation = (route: string, isExternal?: boolean) => {
    if (isExternal) {
      window.open(route, '_blank', 'noopener,noreferrer');
    } else {
      navigate(route);
    }
  };

  const handleTabClick = (tab: NavItem, index: number) => {
    if (tab.items) {
      setActiveTab(activeTab === index ? null : index);
      setMegaMenuOpen(false);
    } else {
      handleNavigation(tab.route, tab.isExternal);
      setActiveTab(null);
      setMegaMenuOpen(false);
    }
  };

  const handleMegaMenuToggle = () => {
    setMegaMenuOpen(!megaMenuOpen);
    setActiveTab(null);
  };

  return (
    <div id="menu">
      <Nav>
        <NavList>
          {visibleItems.map((item, i) => (
            <NavListItem
              key={item.label}
              isActive={activeTab === i}
              onClick={() => handleTabClick(item, i)}
            >
              {item.label} {item.items && <img src={ArrowDown} alt="Opções" />}
              {activeTab === i && item.items && (
                <NavListSubMenu
                  id="submenu"
                  ref={subMenuRef}
                  onClick={(e) => e.stopPropagation()}
                >
                  {item.items.map((navItem) => (
                    <SubMenuItem
                      key={navItem.label}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigation(navItem.route, navItem.isExternal);
                        setActiveTab(null);
                      }}
                    >
                      {navItem.label}
                    </SubMenuItem>
                  ))}
                </NavListSubMenu>
              )}
            </NavListItem>
          ))}
          {hasMegaMenu && (
            <MegaMenu
              items={overflowItems}
              isActive={megaMenuOpen}
              onToggle={handleMegaMenuToggle}
              onClose={emptyActiveTab}
            />
          )}
        </NavList>
      </Nav>
    </div>
  );
};
